/*!
 * Copyright © 2026 Nail Alliance - North America, Inc.
 * Authors: See snippets/copyright-notices.liquid in the theme.
 * No other use is authorized without a written contract.
 */

if (!customElements.get('color-plus-page')) customElements.define('color-plus-page', class extends HTMLElement {
  async connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const {signal} = this.controller;
    this.mediaBase = new URL(this.dataset.source, document.baseURI);
    this.cards = [...this.querySelectorAll('[data-color-product]')];
    this.grid = this.querySelector('[data-products]');
    this.family = this.hasAttribute('data-landing') ? 'color muse' : null;
    this.personaIndex = 0;
    this.selection = null;
    this.sort = null;
    this.randomCards = null;
    this.mobileToggle = this.querySelector('[data-filter-toggle]');
    this.mobileToggle.hidden = false;
    this.classList.add('is-enhanced');
    this.querySelector('[data-sort-group]').hidden = !this.cards.length;
    this.addEventListener('change', () => this.update(), {signal});
    this.addEventListener('click', event => {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-filter-toggle')) {
        this.setFiltersOpen(button.getAttribute('aria-expanded') !== 'true');
        return;
      }
      if (button.hasAttribute('data-sort')) {
        this.sort = button.dataset.sort;
        this.closeMobileFilters();
      } else if (button.hasAttribute('data-clear-sort')) {
        this.sort = null;
        button.previousElementSibling.focus();
      } else if (button.hasAttribute('data-filter-value')) {
        this.selection = {family: button.dataset.filterFamily, value: button.dataset.filterValue};
        this.closeMobileFilters();
      } else if (button.hasAttribute('data-clear-filter')) {
        this.selection = null;
        button.previousElementSibling.focus();
      } else if (button.hasAttribute('data-family')) {
        this.family = button.dataset.family;
        this.personaIndex = 0;
        this.randomCards = this.family === 'random' ? this.pickRandomCards() : null;
      } else if (button.hasAttribute('data-persona-prev')) {
        this.personaIndex--;
      } else if (button.hasAttribute('data-persona-next')) {
        this.personaIndex++;
      } else return;
      this.update();
    }, {signal});
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.mobileToggle.getAttribute('aria-expanded') === 'true') {
        this.setFiltersOpen(false);
        this.mobileToggle.focus();
      }
    }, {signal});
    try {
      const response = await fetch(this.dataset.source, {signal});
      if (!response.ok) throw new Error('Unable to load color profiles');
      this.data = await response.json();
      if (signal.aborted) return;
      const preferred = {'color muse': ['warmth', 'trend-forward', 'bold', 'optimism'], 'gel finish': ['solid', 'glitter', 'flash-glam', 'cat-eye']};
      for (const family of Object.keys(preferred)) {
        const available = [...new Set(this.cards.flatMap(card => this.profile(card)[family] || []))].filter(Boolean);
        const options = [...preferred[family].filter(value => available.includes(value)), ...available.filter(value => !preferred[family].includes(value)).sort()];
        const list = this.querySelector('[data-filter-options="' + family + '"]');
        list.replaceChildren();
        for (const value of options) {
          const row = document.createElement('div');
          row.className = 'color-plus-filter-option';
          const choice = document.createElement('button');
          choice.type = 'button';
          choice.dataset.filterFamily = family;
          choice.dataset.filterValue = value;
          choice.setAttribute('aria-pressed', 'false');
          choice.textContent = value === 'flash-glam' ? 'flash glam' : value;
          const clear = document.createElement('button');
          clear.type = 'button';
          clear.className = 'color-plus-filter-clear';
          clear.dataset.clearFilter = '';
          clear.setAttribute('aria-label', 'Clear ' + value + ' filter');
          clear.textContent = '×';
          clear.hidden = true;
          row.append(choice, clear);
          list.append(row);
        }
        list.closest('details').hidden = !options.length;
      }
      this.querySelector('[data-persona-control]').hidden = !this.cards.length;
      this.update();
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('Color filters unavailable; all collection products remain visible.');
    }
  }
  profile(card) {
    return this.data?.filters[card.dataset.sku] || {};
  }
  setFiltersOpen(open) {
    this.mobileToggle.setAttribute('aria-expanded', String(open));
    this.mobileToggle.querySelector('span').textContent = open ? '−' : '+';
    this.classList.toggle('filters-open', open);
  }
  closeMobileFilters() {
    if (window.matchMedia('(max-width: 767px)').matches) {
      this.setFiltersOpen(false);
      this.mobileToggle.focus();
    }
  }
  pickRandomCards() {
    const cards = [...this.cards];
    for (let i = cards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [cards[i], cards[j]] = [cards[j], cards[i]];
    }
    return cards.slice(0, 8);
  }
  update() {
    const systems = this.data?.systems[this.family];
    const names = Object.keys(systems || {});
    this.personaIndex = names.length ? (this.personaIndex + names.length) % names.length : 0;
    const name = names[this.personaIndex];
    const persona = systems?.[name];
    const panel = this.querySelector('[data-persona]');
    if (panel) {
      panel.hidden = !persona;
      if (persona) {
        panel.querySelector('img').src = new URL(persona.image, this.mediaBase).href;
        panel.querySelector('[data-persona-label]').textContent = name;
      }
    }
    const descriptions = {'color muse':'Find your perfect match by exploring our color muses and their shared personalities like Warmth, Trend-forwardness, Courage, and Optimism.','color aura':'Explore our vibrant spectrum by color family to discover the true aura that perfectly matches your current mood and energy.','gel finish':'Complete your look by exploring our range of show-stopping textures, from classic solid colors to dimensional cat eyes and dazzling glitters.',random:'Embrace the element of surprise and discover an unexpected shade for your next manicure.'};
    const intro = this.querySelector('[data-family-description]');
    if (intro) intro.textContent = descriptions[this.family] || '';
    this.querySelectorAll('[data-family]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.family === this.family)));
    this.querySelectorAll('[data-sort], [data-filter-value]').forEach(button => {
      const active = button.hasAttribute('data-sort') ? this.sort === button.dataset.sort : this.selection?.family === button.dataset.filterFamily && this.selection?.value === button.dataset.filterValue;
      button.setAttribute('aria-pressed', String(Boolean(active)));
      button.nextElementSibling.hidden = !active;
    });
    if (!this.grid) return;
    const cards = [...this.cards];
    if (this.sort) cards.sort((a,b) => (this.sort === 'desc' ? -1 : 1) * a.dataset.sku.localeCompare(b.dataset.sku, undefined, {numeric:true}));
    let count = 0;
    cards.forEach(card => {
      const matches = (!this.selection || this.profile(card)[this.selection.family]?.includes(this.selection.value)) && (!persona || persona.skus.includes(card.dataset.sku)) && (!this.randomCards || this.randomCards.includes(card));
      card.hidden = !matches;
      if (matches) count++;
      this.grid.append(card);
    });
    this.grid.classList.toggle('show-personas', this.querySelector('[data-persona-toggle]').checked);
    this.querySelector('[data-results-count]').textContent = count + (count === 1 ? ' product' : ' products');
    this.querySelector('[data-empty]').hidden = count > 0;
  }
  disconnectedCallback() { this.controller?.abort(); }
});
